<template>
  <v-chip
    v-bind="inheritedChipAttrs"
    :append-icon="chipProps.appendIcon"
    :aria-label="useAriaLabel"
    :class="chipClasses"
    :closable="chipProps.closable"
    :color="chipProps.color"
    :data-testid="chipProps.dataTestId"
    :icon="iconOnly && chipProps.icon"
    :label="chipProps.label"
    :size="chipProps.size"
    :tile="chipProps.tile"
    :variant="chipProps.variant"
  >
    <template #prepend>
      <v-icon
        v-if="chipProps.prependIcon"
        :class="{ 'fs-20': chipProps.iconSize === 'small' }"
        :icon="chipProps.prependIcon"
        class="material-icons-outlined me-1"
      />
    </template>
    <span
      v-if="!iconOnly"
      :style="{ fontWeight: CHIP_LABEL_FONT_WEIGHT }"
    >
      {{ displayText }}
    </span>

    <v-icon
      v-if="iconOnly && icon"
      :icon="icon"
      :size="iconSize"
      class="material-icons-outlined"
    />

    <slot name="append" />
  </v-chip>
</template>

<script setup>
import { capitalize as capitalizeString } from '@/plugins/filters';
import { statusColorNameForKey } from '@/shared/composables/useStatusChipColors';

defineOptions({ inheritAttrs: false });

const attrs = useAttrs();

const inheritedChipAttrs = computed(() => {
  const { class: _class, 'data-testid': _dataTestId, ...rest } = attrs;

  return rest;
});

const CHIP_LABEL_FONT_WEIGHT = 500;
const CHIP_SIZE_DEFAULT = 'small';

const ROLE_DEFAULT_PROPS = {
  oldDefaults: {
    color: 'primary',
    label: true,
    variant: 'flat',
  },
  enabledChip: {
    iconSize: 'small',
    label: false,
    variant: 'flat',
    states: {
      active: { color: 'green-darken-1', text: 'Enabled', prependIcon: 'check_circle' },
      inactive: { color: 'grey-darken-1', text: 'Disabled', prependIcon: 'block' },
    },
  },
  enabledLabel: {
    label: true,
    variant: 'tonal',
    states: {
      active: { color: 'green-darken-1', text: 'Enabled' },
      inactive: { color: 'grey-darken-1', text: 'Disabled' },
    },
  },
  publishedChip: {
    dataTestId: 'published-chip',
    iconSize: 'small',
    label: false,
    variant: 'flat',
    states: {
      active: { color: 'green-darken-1', text: 'Published', prependIcon: 'check_circle' },
      inactive: { color: 'grey-darken-1', text: 'Unpublished', prependIcon: 'block' },
    },
  },
  publishedLabel: {
    label: true,
    variant: 'tonal',
    states: {
      active: { color: 'green-darken-1', text: 'Published' },
      inactive: { color: 'grey-darken-1', text: 'Unpublished' },
    },
  },
  plain: {
    color: 'gray',
    customClasses: 'fw-500',
    variant: 'outlined',
  },
  status: {
    label: true,
    variant: 'tonal',
  },
  warning: {
    color: 'warning',
    prependIcon: 'warning_amber',
    variant: 'tonal',
  },
};

const SEMANTIC_CHIP_COLORS_VUETIFY = {
  orange: 'orange-darken-2',
  warning: 'orange-darken-2',
  red: 'red-darken-2',
  blue: 'light-blue-darken-2',
  purple: 'deep-purple-darken-1',
  green: 'green-darken-3',
  grey: 'grey-darken-2',
};

const $bct = inject('translator');

const props = defineProps({
  // "Template" for the chip styling based on function/role
  role: {
    type: String,
    default: 'oldDefaults',
    validator: (value) =>
      [
        'oldDefaults',
        'enabledChip',
        'enabledLabel',
        'plain',
        'publishedChip',
        'publishedLabel',
        'status',
        'warning',
      ].includes(value),
  },
  //
  active: {
    type: Boolean,
    default: null,
  },
  appendIcon: {
    type: String,
    default: null,
  },
  ariaLabel: {
    type: String,
    default: '',
  },
  capitalize: {
    type: Boolean,
    default: false,
  },
  closable: {
    type: Boolean,
    default: false,
  },
  color: {
    type: String,
    default: null,
  },
  customClasses: {
    type: String,
    default: '',
  },
  dataTestId: {
    type: String,
    default: null,
  },
  icon: {
    type: String,
    default: null,
  },
  iconSize: {
    type: [String, Number],
    default: null,
  },
  label: {
    type: Boolean,
    default: null,
  },
  prependIcon: {
    type: String,
    default: null,
  },
  size: {
    type: String,
    default: null,
  },
  text: {
    type: String,
    default: '',
  },
  textTranslate: {
    type: Boolean,
    default: true,
  },
  tile: {
    type: Boolean,
    default: false,
  },
  variant: {
    type: String,
    default: null,
  },
});

const roleDefaults = computed(() => {
  const { states, ...roleProps } = ROLE_DEFAULT_PROPS[props.role];

  if (props.active != null && !states) {
    console.warn(
      `StandardChip: "active" prop has no effect on role="${props.role}" (no states defined)`,
    );
  }

  if (props.active != null && states) {
    const stateKey = props.active ? 'active' : 'inactive';
    return { ...roleProps, ...states[stateKey] };
  }

  return roleProps;
});

const chipColor = computed(() => {
  let color = props.color ?? roleDefaults.value.color;

  if (color == null && props.role === 'status' && props.text) {
    color = statusColorNameForKey(props.text);
  }

  return SEMANTIC_CHIP_COLORS_VUETIFY[color] ?? color;
});

const displayText = computed(() => {
  const rawText = props.text || roleDefaults.value.text;
  if (!rawText) return;

  const translated = props.textTranslate ? $bct(rawText) : rawText;
  if (props.capitalize) return capitalizeString(translated);

  return translated;
});

const iconOnly = computed(() => !displayText.value && !!props.icon);

const chipProps = computed(() => {
  const appendIconValue = props.appendIcon ?? roleDefaults.value.appendIcon;
  const prependIconValue = props.prependIcon ?? roleDefaults.value.prependIcon;

  return {
    appendIcon: appendIconValue && !iconOnly.value ? appendIconValue : undefined,
    closable: props.closable ?? roleDefaults.value.closable ?? false,
    color: chipColor.value,
    customClasses: props.customClasses || roleDefaults.value.customClasses || '',
    dataTestId: props.dataTestId ?? attrs['data-testid'] ?? roleDefaults.value.dataTestId,
    icon: props.icon,
    iconSize: props.iconSize ?? roleDefaults.value.iconSize ?? props.size ?? 'default',
    label: props.label ?? roleDefaults.value.label ?? false,
    prependIcon: prependIconValue && !iconOnly.value ? prependIconValue : undefined,
    size: props.size ?? roleDefaults.value.size ?? CHIP_SIZE_DEFAULT,
    tile: props.tile,
    variant: props.variant ?? roleDefaults.value.variant,
  };
});

const chipClasses = computed(() => [attrs.class, chipProps.value.customClasses].filter(Boolean));

const useAriaLabel = computed(() => {
  if (iconOnly.value && !props.ariaLabel) {
    console.warn('Icon-only chips require an aria-label for accessibility compliance');
  }

  if (props.ariaLabel) return $bct(props.ariaLabel);

  return displayText.value;
});
</script>
